import { logSentryEvent } from './sentry';

const SLOW_LCP_THRESHOLD_MS = 4_000;

let rumStarted = false;

export const startWebsiteRum = (): void => {
    if (rumStarted || typeof window === 'undefined') {
        return;
    }
    rumStarted = true;

    const emitPageView = () => {
        logSentryEvent('rum.page_view', {
            event: 'page_view',
            area: 'rum',
            route: sanitizeCurrentRoute(),
            ...collectEnvironmentAttributes(),
            ...collectNavigationTimingAttributes(),
            ...collectResourceTimingAttributes(),
        });
    };

    if (document.readyState === 'complete') {
        window.setTimeout(emitPageView, 0);
    } else {
        window.addEventListener('load', emitPageView, { once: true });
    }

    window.addEventListener('popstate', emitPageView);
    observeWebVitals();
};

function observeWebVitals(): void {
    const createObserver = (type: string, callback: PerformanceObserverCallback) => {
        if (typeof PerformanceObserver === 'undefined') {
            return;
        }
        try {
            const observer = new PerformanceObserver(callback);
            observer.observe({ type, buffered: true });
        } catch {
            // Browser support varies; missing metrics are acceptable.
        }
    };

    createObserver('largest-contentful-paint', (list) => {
        for (const entry of list.getEntries()) {
            const value = Math.round(entry.startTime);
            const attributes = {
                event: 'web_vital',
                area: 'rum',
                route: sanitizeCurrentRoute(),
                'metric.name': 'LCP',
                'metric.value': value,
                'metric.unit': 'ms',
                'metric.rating': value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor',
            };
            logSentryEvent('rum.web_vital', attributes);
            if (value > SLOW_LCP_THRESHOLD_MS) {
                logSentryEvent('rum.slow_trace', {
                    ...attributes,
                    event: 'slow_trace',
                    'trace.reason': 'slow_lcp',
                    ...collectNavigationTimingAttributes(),
                    ...collectResourceTimingAttributes(),
                }, 'warn');
            }
        }
    });

    let cls = 0;
    createObserver('layout-shift', (list) => {
        for (const entry of list.getEntries()) {
            const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
            if (!shift.hadRecentInput) {
                cls += shift.value ?? 0;
            }
        }
        logSentryEvent('rum.web_vital', {
            event: 'web_vital',
            area: 'rum',
            route: sanitizeCurrentRoute(),
            'metric.name': 'CLS',
            'metric.value': Number(cls.toFixed(4)),
            'metric.unit': 'score',
            'metric.rating': cls <= 0.1 ? 'good' : cls <= 0.25 ? 'needs-improvement' : 'poor',
        });
    });

    let maxInp = 0;
    createObserver('event', (list) => {
        for (const entry of list.getEntries()) {
            const eventTiming = entry as PerformanceEntry & { duration?: number };
            maxInp = Math.max(maxInp, eventTiming.duration ?? 0);
        }
        if (maxInp > 0) {
            logSentryEvent('rum.web_vital', {
                event: 'web_vital',
                area: 'rum',
                route: sanitizeCurrentRoute(),
                'metric.name': 'INP',
                'metric.value': Math.round(maxInp),
                'metric.unit': 'ms',
                'metric.rating': maxInp <= 200 ? 'good' : maxInp <= 500 ? 'needs-improvement' : 'poor',
            });
        }
    });
}

function sanitizeCurrentRoute(): string {
    return `${window.location.pathname}${window.location.hash ? `#${window.location.hash.replace(/^#/, '')}` : ''}`
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ':id')
        .replace(/\b\d{6,}\b/g, ':id')
        .replace(/\/tasks\/[^/?#]+/gi, '/tasks/:id')
        .replace(/\/requests\/[^/?#]+/gi, '/requests/:id')
        .replace(/\/workspaces\/[^/?#]+/gi, '/workspaces/:id');
}

function collectEnvironmentAttributes(): Record<string, unknown> {
    const connection = (navigator as Navigator & {
        connection?: { effectiveType?: string; rtt?: number; downlink?: number };
    }).connection;
    return {
        'environment.language': navigator.language || null,
        'environment.timezone': Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        'network.effective_type': connection?.effectiveType,
        'network.rtt': connection?.rtt,
        'network.downlink': connection?.downlink,
    };
}

function collectNavigationTimingAttributes(): Record<string, unknown> {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!navigation) {
        return {};
    }
    return {
        'navigation.dns_ms': durationBetween(navigation.domainLookupStart, navigation.domainLookupEnd),
        'navigation.tcp_ms': durationBetween(navigation.connectStart, navigation.connectEnd),
        'navigation.tls_ms': navigation.secureConnectionStart > 0
            ? durationBetween(navigation.secureConnectionStart, navigation.connectEnd)
            : null,
        'navigation.ttfb_ms': durationBetween(navigation.requestStart, navigation.responseStart),
        'navigation.transfer_ms': durationBetween(navigation.responseStart, navigation.responseEnd),
        'navigation.load_ms': durationBetween(navigation.startTime, navigation.loadEventEnd),
    };
}

function collectResourceTimingAttributes(): Record<string, unknown> {
    const grouped = new Map<string, { count: number; total: number; max: number }>();
    for (const entry of performance.getEntriesByType('resource') as PerformanceResourceTiming[]) {
        const type = entry.initiatorType === 'script'
            ? 'script'
            : entry.initiatorType === 'css' || entry.initiatorType === 'link'
                ? 'css'
                : entry.initiatorType === 'font'
                    ? 'font'
                    : entry.initiatorType === 'img'
                        ? 'image'
                        : 'other';
        const current = grouped.get(type) ?? { count: 0, total: 0, max: 0 };
        const duration = Math.round(entry.duration || 0);
        current.count += 1;
        current.total += duration;
        current.max = Math.max(current.max, duration);
        grouped.set(type, current);
    }

    const attributes: Record<string, unknown> = {};
    for (const [type, value] of grouped) {
        attributes[`resources.${type}.count`] = value.count;
        attributes[`resources.${type}.total_duration_ms`] = value.total;
        attributes[`resources.${type}.max_duration_ms`] = value.max;
    }
    return attributes;
}

function durationBetween(start: number, end: number): number | null {
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return null;
    }
    return Math.round(end - start);
}
